import styled from '@emotion/styled';
import { PerformanceReportDimension } from '@kaizenplatform/hugot-entity';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  Typography,
} from '@material-ui/core';
import { ClassAttributes, createElement, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import { compose, StateHandler, withState } from 'recompose';

import { PerformanceReport } from '../../../generated/graphql';
import { SPACING_UNIT } from '../../constant/spacing';
import _HorizontalRadioGroup from '../primitive/HorizontalRadioGroup';
import LfToBr from '../primitive/LfToBr';

interface Props {
  open: boolean;
  report: PerformanceReport;
  onRequestClose: MouseEventHandler<HTMLElement>;
  onClickSubmitButton: (e: any, dimension: PerformanceReportDimension) => void;
  className?: string;
}

type StateAndStateHandler = {
  selectedDimension: PerformanceReportDimension;
  setSelectedDimension: StateHandler<PerformanceReportDimension>;
};

const PerformanceReportExportingDialog = compose<ClassAttributes<HTMLElement> & Props & StateAndStateHandler, Props>(
  withState('selectedDimension', 'setSelectedDimension', ({ report }: Props) => report.dimensions[0]),
)(({ open, report, onRequestClose, onClickSubmitButton, selectedDimension, setSelectedDimension, className }) => {
  const ariaLabelledBy = `performance-report-exporting-dialog-title-${report.id}`;
  const ariaDesscribedBy = `performance-report-exporting-dialog-description-${report.id}`;

  return (
    <Dialog
      open={open}
      onClose={onRequestClose}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDesscribedBy}
      className={className}
    >
      <DialogTitle id={ariaLabelledBy}>
        <FormattedMessage id="performance_report_exporting_dialog.title" />
      </DialogTitle>

      <DialogContent>
        <DialogContentText id={ariaDesscribedBy}>
          <FormattedMessage id="performance_report_exporting_dialog.description_text">
            {(text: string) => <LfToBr text={text} />}
          </FormattedMessage>
        </DialogContentText>

        <ContentDivider />

        <DimensionSelector>
          <InputLabel shrink>
            <FormattedMessage id="performance_report_exporting_dialog.options.dimension" />
          </InputLabel>
          <HorizontalRadioGroup
            value={selectedDimension}
            onChange={(_, value) => {
              setSelectedDimension(value);
            }}
          >
            {report.dimensions
              .filter((dimension) => !(report.adAccount.network === 'yahoo' && dimension === 'placement'))
              .map((dimension) => (
                <DimensionFormControlLabel
                  key={'dimension_' + dimension}
                  value={dimension}
                  control={<Radio />}
                  label={
                    <Typography>
                      <FormattedMessage id={`performance_report_dimension.${dimension.toLowerCase()}`} />
                    </Typography>
                  }
                />
              ))}
          </HorizontalRadioGroup>
        </DimensionSelector>
      </DialogContent>

      <DialogActions>
        <Button onClick={onRequestClose}>
          <FormattedMessage id="performance_report_exporting_dialog.actions.cancel" />
        </Button>

        <Button
          onClick={(e) => {
            onClickSubmitButton(e, selectedDimension);
          }}
          autoFocus
        >
          <FormattedMessage id="performance_report_exporting_dialog.actions.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const DimensionSelector = styled(FormControl)({
  margin: `${SPACING_UNIT}px 0`,
});

const ContentDivider = styled(Divider)({
  margin: `${SPACING_UNIT}px 0`,
});

const DimensionFormControlLabel = styled(FormControlLabel)({
  margin: `${SPACING_UNIT}px 0`,
});

const HorizontalRadioGroup = styled(_HorizontalRadioGroup)({
  marginTop: SPACING_UNIT,
});

export default PerformanceReportExportingDialog;
